import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	Chip,
	Skeleton,
	Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateEuDdpOrderFieldValueRequest } from 'redux/reducers/euDdp/reducer';
import {
	getEuDdpOrder,
	getEuDdpOrderIsLoad,
	getEuDdpUpdateOrderFieldValueIsLoad,
} from 'redux/reducers/euDdp/selectors';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { EditableField } from 'components/Shipment/DeliveryInformation/EditableField';

const buildValidationPattern = (expression: string | null | undefined): RegExp | null => {
	if (!expression) return null;
	try {
		return new RegExp(expression);
	} catch {
		return null;
	}
};

const vatValidatedChipColor = (
	validated: string | boolean | null | undefined,
): 'warning' | 'success' => {
	if (validated === 'not_validated' || validated === false) return 'warning';
	return 'success';
};

const vatValidatedChipLabel = (validated: string | boolean | null | undefined): string => {
	if (typeof validated === 'string') return validated.replace(/_/g, ' ');
	if (validated === true) return 'Validated';
	return 'Not validated';
};

export const DeliveryInformation: FC = () => {
	const dispatch = useDispatch();
	const order = useSelector(getEuDdpOrder);
	const orderIsLoading = useSelector(getEuDdpOrderIsLoad);
	const updateOrderFieldsLoading = useSelector(getEuDdpUpdateOrderFieldValueIsLoad);

	const editableFirstName = order?.fieldsConfig.find(
		(field) => field.tableFieldName === 'recipient_first_name',
	);
	const editableLastName = order?.fieldsConfig.find(
		(field) => field.tableFieldName === 'recipient_last_name',
	);
	const editableEmail = order?.fieldsConfig.find(
		(field) => field.tableFieldName === 'delivery_email',
	);
	const editablePhone = order?.fieldsConfig.find(
		(field) => field.tableFieldName === 'delivery_phone',
	);
	const editableCity = order?.fieldsConfig.find(
		(field) => field.tableFieldName === 'delivery_city',
	);
	const editableAddressLine1 = order?.fieldsConfig.find(
		(field) => field.tableFieldName === 'delivery_address_line_1',
	);
	const editableAddressLine2 = order?.fieldsConfig.find(
		(field) => field.tableFieldName === 'delivery_address_line_2',
	);
	const editableZipCode = order?.fieldsConfig.find(
		(field) => field.tableFieldName === 'delivery_postal_code',
	);
	const editableProvince = order?.fieldsConfig.find(
		(field) => field.tableFieldName === 'delivery_state_province',
	);

	const [firstName, setFirstName] = useState('');
	const [firstNameError, setFirstNameError] = useState('');
	const [lastName, setLastName] = useState('');
	const [lastNameError, setLastNameError] = useState('');
	const [email, setEmail] = useState('');
	const [emailError, setEmailError] = useState('');
	const [phone, setPhone] = useState('');
	const [phoneError, setPhoneError] = useState('');
	const [city, setCity] = useState('');
	const [cityError, setCityError] = useState('');
	const [addressLine1, setAddressLine1] = useState('');
	const [addressLine1Error, setAddressLine1Error] = useState('');
	const [addressLine2, setAddressLine2] = useState('');
	const [addressLine2Error, setAddressLine2Error] = useState('');
	const [zipCode, setZipCode] = useState('');
	const [zipCodeError, setZipCodeError] = useState('');
	const [province, setProvince] = useState('');
	const [provinceError, setProvinceError] = useState('');

	const handleUpdateValues = () => {
		if (!order?.order.id) return;
		const fieldValues: Record<string, string> = {};
		if (editableFirstName?.tableName) {
			fieldValues[editableFirstName.tableFieldName] = firstName;
		}
		if (editableLastName?.tableName) {
			fieldValues[editableLastName.tableFieldName] = lastName;
		}
		if (editableEmail?.tableName) {
			fieldValues[editableEmail.tableFieldName] = email;
		}
		if (editablePhone?.tableName) {
			fieldValues[editablePhone.tableFieldName] = phone;
		}
		if (editableCity?.tableName) {
			fieldValues[editableCity.tableFieldName] = city;
		}
		if (editableAddressLine1?.tableName) {
			fieldValues[editableAddressLine1.tableFieldName] = addressLine1;
		}
		if (editableAddressLine2?.tableName) {
			fieldValues[editableAddressLine2.tableFieldName] = addressLine2;
		}
		if (editableZipCode?.tableName) {
			fieldValues[editableZipCode.tableFieldName] = zipCode;
		}
		if (editableProvince?.tableName) {
			fieldValues[editableProvince.tableFieldName] = province;
		}

		dispatch(
			updateEuDdpOrderFieldValueRequest({
				orderId: order.order.id,
				fieldValues,
			}),
		);
	};

	useEffect(() => {
		if (!order?.order) return;
		if (editableFirstName) {
			setFirstName(editableFirstName.value);
		}
		if (editableLastName) {
			setLastName(editableLastName.value);
		}
		if (editablePhone) {
			setPhone(editablePhone.value);
		}
		if (editableEmail) {
			setEmail(editableEmail.value);
		}
		if (editableCity) {
			setCity(editableCity.value);
		}
		if (editableAddressLine1) {
			setAddressLine1(editableAddressLine1.value);
		}
		if (editableAddressLine2) {
			setAddressLine2(editableAddressLine2.value);
		}
		if (editableZipCode) {
			setZipCode(editableZipCode.value);
		}
		if (editableProvince) {
			setProvince(editableProvince.value);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [order]);

	return (
		<div>
			{order && !orderIsLoading ? (
				<Card
					sx={{
						mb: 2,
						'&': {
							borderRadius: '12px',
						},
					}}
				>
					<CardContent>
						<Typography sx={{ fontSize: 14 }} color="primary" gutterBottom>
							Delivery Information
						</Typography>
						{order?.order?.valuesStatus === 'Unprocessed' && (
							<>
								<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
									{editableFirstName && (
										<EditableField
											firstValue={editableFirstName.value}
											label="First Name"
											value={firstName}
											error={firstNameError}
											setValue={setFirstName}
											setError={setFirstNameError}
											isRequired
											validationPattern={buildValidationPattern(
												editableFirstName.regularExpression,
											)}
											errorComment={editableFirstName.comment}
										/>
									)}

									{editableLastName && (
										<EditableField
											firstValue={editableLastName.value}
											label="Last Name"
											value={lastName}
											error={lastNameError}
											setValue={setLastName}
											setError={setLastNameError}
											isRequired
											validationPattern={buildValidationPattern(editableLastName.regularExpression)}
											errorComment={editableLastName.comment}
										/>
									)}
								</Box>
								{(editableEmail || editablePhone) && (
									<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
										{editableEmail && (
											<EditableField
												firstValue={editableEmail.value}
												label="Email"
												type="email"
												value={email}
												error={emailError}
												setValue={setEmail}
												setError={setEmailError}
												isRequired
												validationPattern={buildValidationPattern(editableEmail.regularExpression)}
												errorComment={editableEmail.comment}
											/>
										)}
										{editablePhone && (
											<EditableField
												firstValue={editablePhone.value}
												label="Phone"
												type="phone"
												value={phone}
												error={phoneError}
												setValue={setPhone}
												setError={setPhoneError}
												isRequired
												validationPattern={buildValidationPattern(editablePhone.regularExpression)}
												errorComment={editablePhone.comment}
											/>
										)}
										{(!editableEmail || !editablePhone) && <div style={{ width: '100%' }}> </div>}
									</Box>
								)}
								{editableCity && (
									<Box sx={{ display: 'flex' }}>
										<EditableField
											firstValue={editableCity.value}
											label="City"
											value={city}
											error={cityError}
											setValue={setCity}
											setError={setCityError}
											isRequired
											validationPattern={buildValidationPattern(editableCity.regularExpression)}
											errorComment={editableCity.comment}
										/>
									</Box>
								)}
								{editableAddressLine1 && (
									<Box sx={{ display: 'flex' }}>
										<EditableField
											firstValue={editableAddressLine1.value}
											label="Address Line 1"
											value={addressLine1}
											error={addressLine1Error}
											setValue={setAddressLine1}
											setError={setAddressLine1Error}
											isRequired
											validationPattern={buildValidationPattern(
												editableAddressLine1.regularExpression,
											)}
											errorComment={editableAddressLine1.comment}
										/>
									</Box>
								)}
								{editableAddressLine2 && (
									<Box sx={{ display: 'flex' }}>
										<EditableField
											firstValue={editableAddressLine2.value}
											label="Address Line 2"
											value={addressLine2}
											error={addressLine2Error}
											setValue={setAddressLine2}
											setError={setAddressLine2Error}
											validationPattern={buildValidationPattern(
												editableAddressLine2.regularExpression,
											)}
											errorComment={editableAddressLine2.comment}
											isRequired={false}
										/>
									</Box>
								)}
								{(editableZipCode || editableProvince) && (
									<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
										{editableZipCode && (
											<EditableField
												firstValue={editableZipCode.value}
												label="Zip Code"
												value={zipCode}
												error={zipCodeError}
												setValue={setZipCode}
												setError={setZipCodeError}
												isRequired
												validationPattern={buildValidationPattern(
													editableZipCode.regularExpression,
												)}
												errorComment={editableZipCode.comment}
											/>
										)}
										{editableProvince && (
											<EditableField
												firstValue={editableProvince.value}
												label="Province"
												value={province}
												error={provinceError}
												setValue={setProvince}
												setError={setProvinceError}
												validationPattern={buildValidationPattern(
													editableProvince.regularExpression,
												)}
												errorComment={editableProvince.comment}
												isRequired={false}
											/>
										)}
									</Box>
								)}
							</>
						)}

						{(!editableFirstName ||
							!editableLastName ||
							order?.order?.valuesStatus !== 'Unprocessed') && (
							<>
								<Typography sx={{ mt: 1.5 }} color="text.secondary">
									Customer:
								</Typography>
								<Typography variant="h6" component="div">
									{order?.order.deliveryPerson}
								</Typography>
							</>
						)}

						{order?.order.recipientCompanyName && (
							<>
								<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
									Company name:
								</Typography>
								<Typography>{order?.order.recipientCompanyName}</Typography>
							</>
						)}
						{order?.order.recipientCompanyVat && (
							<>
								<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
									Company VAT number:
								</Typography>
								<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
									<Typography>{order?.order.recipientCompanyVat}</Typography>
									<Chip
										label={vatValidatedChipLabel(order.order.recipientCompanyVatValidated)}
										color={vatValidatedChipColor(order.order.recipientCompanyVatValidated)}
										size="small"
										sx={{
											p: '0 5px',
											ml: 1,
											fontSize: '.7rem',
											height: '20px',
										}}
									/>
								</Box>
							</>
						)}
						{(!editableEmail || order?.order?.valuesStatus !== 'Unprocessed') && (
							<>
								<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
									Email:
								</Typography>
								<Typography>{order?.order.deliveryEmail || '—'}</Typography>{' '}
							</>
						)}

						{(!editablePhone || order?.order?.valuesStatus !== 'Unprocessed') && (
							<>
								<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
									Phone number:
								</Typography>
								<Typography>{order?.order.deliveryPhone || '—'}</Typography>{' '}
							</>
						)}

						<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
							Delivery Country:
						</Typography>
						<CountryWithFlag countryCode={order?.order.deliveryCountry} />

						{order.order.valuesStatus !== 'Unprocessed' && order.order.deliveryAddressLine1 && (
							<>
								<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
									Delivery Address:
								</Typography>
								<Typography>
									{order.order.deliveryAddressLine1}
									{order.order.deliveryAddressLine2 ? `, ${order.order.deliveryAddressLine2}` : ''}
								</Typography>
								<Typography>
									{[
										order.order.deliveryPostalCode,
										order.order.deliveryCity,
										order.order.deliveryStateProvince,
									]
										.filter(Boolean)
										.join(', ')}
								</Typography>
							</>
						)}

						<Typography sx={{ mt: 1.5 }} variant="body2" color="text.secondary">
							Delivery Cost:
						</Typography>
						<AmountAndCurrency
							amount={order?.order.deliveryCost ?? 0}
							currency={order?.order.paymentCurrency}
							size={1}
						/>
					</CardContent>
					{order?.order?.valuesStatus === 'Unprocessed' && (
						<CardActions>
							<Button
								variant="contained"
								color="primary"
								size="small"
								sx={{
									borderRadius: '12px!important',
									textTransform: 'none',
									maxHeight: '24px',
								}}
								disabled={
									!!firstNameError ||
									!!lastNameError ||
									!!emailError ||
									!!phoneError ||
									!!cityError ||
									!!addressLine1Error ||
									!!addressLine2Error ||
									!!zipCodeError ||
									!!provinceError ||
									updateOrderFieldsLoading ||
									((editableFirstName?.value ?? '') === firstName &&
										(editableLastName?.value ?? '') === lastName &&
										(editableEmail?.value ?? '') === email &&
										(editablePhone?.value ?? '') === phone &&
										(editableCity?.value ?? '') === city &&
										(editableAddressLine1?.value ?? '') === addressLine1 &&
										(editableAddressLine2?.value ?? '') === addressLine2 &&
										(editableZipCode?.value ?? '') === zipCode &&
										(editableProvince?.value ?? '') === province)
								}
								onClick={handleUpdateValues}
							>
								Update Delivery Information
							</Button>
						</CardActions>
					)}
				</Card>
			) : (
				<Skeleton variant="rectangular" height="300px" sx={{ borderRadius: '12px', mb: 2 }} />
			)}
		</div>
	);
};
